import {
  Box,
  CircularProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import isEmpty from "lodash/isEmpty";

import { useCallback, useMemo, useState } from "react";
import TableNoDataOverlay from "../../../common/components/table/TableNoDataOverlay";
import { useDeploymentHealthTableRows } from "../hooks/useDeploymentHealthTableRows";
import DeploymentHealthTableBodyRow from "./table/DeploymentHealthTableBodyRow";
import DeploymentHealthTableHeadRow from "./table/DeploymentHealthTableHeadRow";
import ToggleTrackingDialog from "./table/ToggleTrackingDialog";

export interface DialogState {
  isOpen: boolean;
  targetLabel: string;
  entityType: "location" | "node" | "device" | "model";
  entityId: string;
  locationId: string;
  initialIsExpectedOnline?: boolean;
  leafPk?: string;
  leafSk?: string;
  existingReason?: string;
  existingAssignedWatcher?: string;
  allowErrorReason: boolean;
}

const initialDialogState: DialogState = {
  isOpen: false,
  targetLabel: "",
  entityType: "location",
  entityId: "",
  locationId: "",
  initialIsExpectedOnline: undefined,
  leafPk: undefined,
  leafSk: undefined,
  existingReason: undefined,
  existingAssignedWatcher: undefined,
  allowErrorReason: false,
};

const DeploymentHealthTable = (): JSX.Element => {
  const { data, loading, fetchingMore } = useDeploymentHealthTableRows();
  const [dialogState, setDialogState] =
    useState<DialogState>(initialDialogState);

  const facilitiesHealthPercent = useMemo(() => {
    if (loading || fetchingMore) return "";

    const rows = data ?? [];
    const isGrey = (row: (typeof rows)[number]): boolean => {
      const nodeAllExpectedOffline = row.nodes.every(
        n => n.isExpectedOnline === false
      );
      const deviceAllExpectedOffline = row.devices.every(
        d => d.isExpectedOnline === false
      );
      const serviceAllExpectedOffline = row.services.every(
        s => s.isExpectedOnline === false
      );

      return (
        nodeAllExpectedOffline &&
        deviceAllExpectedOffline &&
        serviceAllExpectedOffline
      );
    };

    // If all locations are grey (no operating facilities), return special messaging
    const allGrey = rows.length > 0 && rows.every(isGrey);

    if (allGrey) {
      return "No operating facilities";
    }

    const considered = rows.filter(r => !isGrey(r));
    const green = considered.filter(r => r.locationStatus).length;
    const total = considered.length; // green + red

    if (total === 0) return "0%";

    return `${Math.round((green / total) * 100)}%`;
  }, [data, loading, fetchingMore]);

  const overallTooltip =
    "Calculated as: among facilities with at least one expected-online node/device/service, the percent that are healthy (green).\n" +
    "A facility is healthy when all expected-online nodes, devices, and services are online.\n" +
    "Facilities where everything is expected offline are excluded (grey).";

  const handleOpenDialog = useCallback(
    (params: Omit<DialogState, "isOpen">) => {
      setDialogState({ ...params, isOpen: true });
    },
    []
  );

  const handleCloseDialog = useCallback(() => {
    setDialogState(initialDialogState);
  }, []);

  return (
    <Paper>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 2,
        }}
      >
        <Tooltip
          title={<Box sx={{ whiteSpace: "pre-line" }}>{overallTooltip}</Box>}
          arrow
          placement="right"
        >
          <Typography variant="h6" sx={{ cursor: "help" }}>
            Overall Facility Health:{" "}
            {!loading && !fetchingMore && facilitiesHealthPercent}
          </Typography>
        </Tooltip>
        {(loading || fetchingMore) && <CircularProgress size={20} />}
      </Box>
      <Table>
        <DeploymentHealthTableHeadRow />
        <TableBody>
          {data &&
            !loading &&
            data.map((row): JSX.Element => {
              return (
                <DeploymentHealthTableBodyRow
                  key={row.locationId}
                  row={row}
                  onOpenDialog={handleOpenDialog}
                />
              );
            })}

          {loading && (
            <TableRow>
              <TableCell colSpan={6}>
                {[...Array(6)].map(
                  (_, index): JSX.Element => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      sx={{ my: 3 }}
                    />
                  )
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isEmpty(data) && !loading && <TableNoDataOverlay />}
      {fetchingMore && (
        <Box
          sx={{ display: "flex", flexDirection: "column", py: 1, padding: 2 }}
        >
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Box>
      )}

      <ToggleTrackingDialog
        open={dialogState.isOpen}
        targetLabel={dialogState.targetLabel}
        entityType={dialogState.entityType}
        onClose={handleCloseDialog}
        entityId={dialogState.entityId}
        locationId={dialogState.locationId}
        initialIsExpectedOnline={dialogState.initialIsExpectedOnline}
        leafPk={dialogState.leafPk}
        leafSk={dialogState.leafSk}
        existingReason={dialogState.existingReason}
        existingAssignedWatcher={dialogState.existingAssignedWatcher}
        allowErrorReason={dialogState.allowErrorReason}
      />
    </Paper>
  );
};

export default DeploymentHealthTable;
